import { type, type Type } from 'arktype'

export type Siret = string & { __siret: void }

export const siretRegex: RegExp = /^[0-9]{14}$/

export function isLuhnValid(digits: string): boolean {
  if (/^0+$/.test(digits)) return false
  let sum = 0
  for (let i = 0; i < digits.length; i++) {
    const char = digits[digits.length - 1 - i]
    if (char === undefined) return false
    let digit = parseInt(char, 10)
    if (i % 2 === 1) {
      digit *= 2
      if (digit > 9) digit -= 9
    }
    sum += digit
  }
  return sum % 10 === 0
}

export function parseSiret(input: unknown): Siret | null {
  if (typeof input !== 'string') {
    return null
  }
  const trimmedInput = input.replace(/\s/g, '').trim()
  if (siretRegex.test(trimmedInput)) {
    return trimmedInput as Siret
  }
  return null
}

export const siretStringSchema: Type<string> = type('string').narrow((s, ctx) =>
  parseSiret(s) !== null ? true : ctx.mustBe('a valid siret'),
)

export class InvalidSiretError extends Error {
  constructor() {
    super('Input is not a valid siret')
  }
}

export function loadSiret(input: unknown): Siret {
  const siret = parseSiret(input)
  if (siret === null) {
    throw new InvalidSiretError()
  }
  return siret
}

export type JsonableSiretData = { type: 'provided'; siret: string } | { type: 'later' }
export const siretDataSchema: Type<JsonableSiretData> = type({ type: "'provided'", siret: siretStringSchema }).or({
  type: "'later'",
})

export function extractSiretFromData(data?: JsonableSiretData | string | null): Siret | undefined {
  if (typeof data === 'string') return parseSiret(data) ?? undefined
  return !data || data.type === 'later' ? undefined : (parseSiret(data.siret) ?? undefined)
}
